import { API_BASE_URL } from '../apiBaseUrl';

const buildUrl = (path, params) => {
  const url = new URL(`${API_BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      url.searchParams.set(key, String(value));
    });
  }
  return url.toString();
};

const waitForCurrentUser = (firebase, timeoutMs = 10000) =>
  new Promise((resolve, reject) => {
    const auth = firebase?.auth;
    if (!auth?.onAuthStateChanged) {
      reject(new Error('Firebase auth is not available.'));
      return;
    }

    if (auth.currentUser) {
      resolve(auth.currentUser);
      return;
    }

    const timer = setTimeout(() => {
      unsubscribe?.();
      reject(new Error('No signed-in Firebase user found.'));
    }, timeoutMs);

    const unsubscribe = auth.onAuthStateChanged(
      (user) => {
        if (!user) return;
        clearTimeout(timer);
        unsubscribe();
        resolve(user);
      },
      (error) => {
        clearTimeout(timer);
        unsubscribe();
        reject(error);
      },
    );
  });

const getIdToken = async (firebase) => {
  const currentUser = firebase?.auth?.currentUser || (await waitForCurrentUser(firebase));
  return currentUser.getIdToken();
};

const parseError = async (response) => {
  try {
    const data = await response.json();
    return data;
  } catch (_err) {
    return { error: response.statusText };
  }
};

const support404Fallback = (path) => {
  const normalizedPath = String(path || '');
  if (normalizedPath.endsWith('/status')) {
    return { connected: false, unavailable: true };
  }
  if (normalizedPath.endsWith('/articles')) {
    return { items: [], unavailable: true };
  }
  if (normalizedPath.endsWith('/system-status')) {
    return { components: [{ name: 'Platform', status: 'Operational' }], unavailable: true };
  }
  if (normalizedPath.endsWith('/tickets')) {
    return { items: [], unavailable: true };
  }
  if (normalizedPath.includes('/tickets/')) {
    return {
      id: normalizedPath.split('/tickets/')[1] || '',
      messages: [],
      status: 'open',
      priority: 'normal',
      unavailable: true,
    };
  }
  return { unavailable: true };
};

const apiFetch = async (firebase, path, options = {}, params) => {
  const token = await getIdToken(firebase);
  const headers = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  let response;
  try {
    response = await fetch(buildUrl(path, params), {
      ...options,
      headers,
    });
  } catch (err) {
    const error = new Error(
      `Cannot reach Support backend at ${API_BASE_URL}. Make sure the server is running and REACT_APP_API_BASE_URL is correct.`,
    );
    error.code = 'SUPPORT_BACKEND_UNREACHABLE';
    error.status = 0;
    error.cause = err;
    throw error;
  }

  if (!response.ok) {
    if (response.status === 404) {
      return support404Fallback(path);
    }

    const errorPayload = await parseError(response);
    const error = new Error(errorPayload.error || 'Support API request failed.');
    error.code = errorPayload.code || 'SUPPORT_API_ERROR';
    error.status = response.status;
    throw error;
  }

  if (response.status === 204) return null;
  return response.json();
};

export const supportStatus = async (firebase) =>
  apiFetch(firebase, '/api/support/status', { method: 'GET' });

export const supportArticlesList = async (firebase) =>
  apiFetch(firebase, '/api/support/articles', { method: 'GET' });

export const supportFeedbackCreate = async (firebase, payload) =>
  apiFetch(firebase, '/api/support/feedback', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const supportSystemStatus = async (firebase) =>
  apiFetch(firebase, '/api/support/system-status', { method: 'GET' });

export const supportTicketsList = async (firebase, params = {}) =>
  apiFetch(firebase, '/api/support/tickets', { method: 'GET' }, params);

export const supportTicketGet = async (firebase, ticketId, params = {}) =>
  apiFetch(firebase, `/api/support/tickets/${ticketId}`, { method: 'GET' }, params);

export const supportTicketCreate = async (firebase, payload) =>
  apiFetch(firebase, '/api/support/tickets', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

export const supportTicketAddMessage = async (firebase, ticketId, payload, params = {}) =>
  apiFetch(firebase, `/api/support/tickets/${ticketId}/messages`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }, params);

export const supportTicketUpdateStatus = async (firebase, ticketId, payload) =>
  apiFetch(firebase, `/api/support/tickets/${ticketId}/status`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

export const supportUploadAttachments = async (firebase, ticketId, files = []) => {
  if (!files.length) return [];
  const token = await getIdToken(firebase);
  const formData = new FormData();
  formData.append('ticketId', ticketId);
  files.forEach((file) => formData.append('files', file));

  const response = await fetch(buildUrl('/api/support/attachments'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const errorPayload = await parseError(response);
    throw new Error(errorPayload.error || 'Support attachment upload failed.');
  }

  const data = await response.json();
  return data.items || [];
};
